import { ref, toValue, watchEffect } from 'vue';
import heic2any from 'heic2any';

type KycFile = {
	cloudStorageUrl: string;
	issuedAt?: string;
	code?: string;
	extRef?: string;
	page?: string;
	isHeic: boolean;
};

type ConvertHeicFilesArgs<T> = {
	files: T[];
	toType: string;
	quality: number;
};

const useHeicConverter = <T>({
	files,
	toType = 'image/jpeg',
	quality = 0.1,
}: ConvertHeicFilesArgs<T>) => {
	const convertedFiles = ref<File[] | KycFile[]>([]);
	const isConvertingFiles = ref(false);
	const errorConvertingFiles = ref(false);

	const convertHeicFiles = async () => {
		await Promise.all(
			toValue(files).map(async (file) => {
				const typeHolder = (file as File).type ?? (file as KycFile).cloudStorageUrl;
				if (!typeHolder?.includes('heic')) return;

				errorConvertingFiles.value = false;
				isConvertingFiles.value = true;

				try {
					const url =
						(file as KycFile).cloudStorageUrl ?? URL.createObjectURL(file as File);
					const heicFile = await fetch(url);
					const heicBlob = await heicFile.blob();
					const convertedBlob = await heic2any({
						blob: heicBlob,
						toType,
						quality,
					});

					const composedFile = (file as KycFile).cloudStorageUrl
						? {
								...(file as KycFile),
								cloudStorageUrl: URL.createObjectURL(convertedBlob as Blob),
						  }
						: {
								name: (file as File).name,
								url: URL.createObjectURL(convertedBlob as Blob),
						  };

					convertedFiles.value.push(composedFile);
				} catch {
					errorConvertingFiles.value = true;
				} finally {
					isConvertingFiles.value = false;
				}
			})
		);
	};

	watchEffect(async () => convertHeicFiles(), {
		flush: 'sync',
	});

	return {
		convertedFiles,
		isConvertingFiles,
		errorConvertingFiles,
	};
};

export default useHeicConverter;
